import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Play, Star, Users, BookOpen, Volume2, VolumeX, X, Maximize2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import LearningTracksSection from '@/components/sections/LearningTracksSection';
import AIEssentialsBundleSection from '@/components/sections/AIEssentialsBundleSection';
import UltimatePromptBundleSection from '@/components/sections/UltimatePromptBundleSection';
import { siteVideoService } from '@/services/siteVideoService';
import { trackVideo, trackCTA, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { metaPixel } from '@/services/metaPixel';

export default function DigitalAcademy() {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isTheaterMode, setIsTheaterMode] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const theaterVideoRef = useRef<HTMLVideoElement>(null);

  // Track scroll depth
  useScrollTracking('Homepage');

  // Track page view on mount
  useEffect(() => {
    // Meta Pixel
    metaPixel.init();
    metaPixel.pageView();
    trackPageView('Homepage');
  }, []);

  // Fetch homepage hero video
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const videoData = await siteVideoService.getSiteVideo('homepage_hero');
        if (videoData.playback_url) {
          setVideoUrl(videoData.playback_url);
        }
      } catch (error) {
        // Log error but don't crash - video is optional, fallback image will show
        console.warn('[Homepage] Failed to load hero video:', error);
      } finally {
        setVideoLoading(false);
      }
    };

    fetchVideo();
  }, []);

  // Handle ESC key to close theater mode
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isTheaterMode) {
        closeTheaterMode();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isTheaterMode]);

  // Prevent body scroll when theater mode is open
  useEffect(() => {
    if (isTheaterMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isTheaterMode]);

  // Toggle mute/unmute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
      // Track mute toggle
      trackVideo.muteToggle('homepage-hero-video', !isMuted);
    }
  };

  // Toggle mute in theater mode
  const toggleTheaterMute = () => {
    if (theaterVideoRef.current) {
      theaterVideoRef.current.muted = !theaterVideoRef.current.muted;
      setIsMuted(!isMuted);
      // Track mute toggle in theater mode
      trackVideo.muteToggle('homepage-hero-video-theater', !isMuted);
    }
  };

  // Open theater mode
  const openTheaterMode = () => {
    setIsTheaterMode(true);
    // Track theater mode open
    trackVideo.theaterModeToggle('homepage-hero-video', true);
    trackVideo.play('homepage-hero-video', videoUrl || '', 'homepage-theater');
    // Pause the small video when opening theater
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Reset to beginning
    }
  };

  // Close theater mode
  const closeTheaterMode = () => {
    setIsTheaterMode(false);
    // Track theater mode close
    trackVideo.theaterModeToggle('homepage-hero-video', false);
    // Stop the theater video
    if (theaterVideoRef.current) {
      theaterVideoRef.current.pause();
      theaterVideoRef.current.currentTime = 0; // Reset to beginning
    }
    // Keep the small video paused (don't auto-resume)
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Reset to beginning
    }
  };

  // Enter fullscreen
  const enterFullscreen = () => {
    if (theaterVideoRef.current) {
      if (theaterVideoRef.current.requestFullscreen) {
        theaterVideoRef.current.requestFullscreen();
        // Track fullscreen toggle
        trackVideo.fullscreenToggle('homepage-hero-video-theater', true);
      }
    }
  };

  // Track CTA clicks
  const handleCtaClick = (ctaId: string, ctaText: string, location: string) => {
    trackCTA.click(ctaId, ctaText, location, undefined);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>AI Mastery Hub - Master AI, Artificial Intelligence Training | Premium Online AI Courses</title>
        <meta name="description" content="Master AI and machine learning with world-class online courses. Join thousands of students transforming their careers with AI Mastery Hub." />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-mastery-hub.com/" />
        <meta property="og:title" content="AI Mastery Hub - Master AI Before It Masters Your Industry" />
        <meta property="og:description" content="Master AI with world-class digital courses. Transform your skills and unlock your potential." />
        <meta property="og:image" content="https://ai-mastery-hub.com/og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://ai-mastery-hub.com/" />
        <meta property="twitter:title" content="AI Mastery Hub - Master AI Before It Masters Your Industry" />
        <meta property="twitter:description" content="Master AI with world-class digital courses." />
        <meta property="twitter:image" content="https://ai-mastery-hub.com/og-image.jpg" />

        {/* Additional SEO */}
        <meta name="keywords" content="AI courses, machine learning, artificial intelligence training, coding bootcamp, investing education, online learning platform, AI mastery, deep learning, neural networks" />
        <meta name="author" content="AI Mastery Hub" />
        <link rel="canonical" href="https://ai-mastery-hub.com/" />

        {/* Schema.org markup for Google */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            "name": "AI Mastery Hub",
            "description": "Master AI with world-class digital courses",
            "url": "https://ai-mastery-hub.com",
            "logo": "https://ai-mastery-hub.com/logo.png",
            "offers": {
              "@type": "Offer",
              "category": "Online Courses"
            }
          })}
        </script>
      </Helmet>

      {/* Navigation */}
      <Navigation/>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
                <span className="text-purple-400">🚀 New courses added weekly</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
                <span className="block">Master AI Before</span>
                <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  It Masters Your Industry
                </span>
              </h1>
              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Master AI with world-class digital courses. 
                Transform your skills and unlock your potential.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/dashboard"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-4 rounded-full font-semibold hover:shadow-2xl hover:shadow-purple-500/50 transition-all text-lg group inline-flex items-center"
                  onClick={() => handleCtaClick('hero-start-learning', 'Start Learning', 'hero')}
                >
                  Start Learning
                  <Play className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/courses"
                  className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all text-lg border border-white/20"
                  onClick={() => handleCtaClick('hero-view-courses', 'View All Courses', 'hero')}
                >
                  View All Courses
                </Link>
              </div>
              <div className="flex items-center space-x-8 mt-12 text-sm">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  <span className="text-gray-400">Growing Community</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span className="text-gray-400">Robust Curriculum</span>
                </div>
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-blue-400" />
                  <span className="text-gray-400">70+ Topics</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              {/* Subtle glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur-3xl opacity-20"></div>

              {/* Video container - clean and minimal with proper aspect ratio */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black/50">
                {videoUrl ? (
                  <div className="relative w-full aspect-video cursor-pointer group" onClick={openTheaterMode}>
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-contain"
                    />
                    {/* Hover overlay to indicate clickable */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/40">
                          <Maximize2 className="w-10 h-10 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop"
                    alt="Hero"
                    className="w-full h-[28rem] object-cover"
                  />
                )}


                {/* Audio toggle button */}
                {videoUrl && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMute();
                    }}
                    className="absolute bottom-6 right-6 w-14 h-14 bg-black/80 backdrop-blur-xl rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl border border-white/20 hover:bg-black/90 z-10"
                  >
                    {isMuted ? (
                      <VolumeX className="w-6 h-6 text-white" />
                    ) : (
                      <Volume2 className="w-6 h-6 text-white" />
                    )}
                  </button>
                )}

                {/* Placeholder play button */}
                {!videoUrl && (
                  <button className="absolute bottom-6 right-6 w-16 h-16 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl">
                    <Play className="w-6 h-6 text-black fill-black ml-1" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories - Now using separate component */}
      {/* <CategoryCards /> */}

      {/* Featured Courses - Now using separate component */}
      {/* <FeaturedCourses /> */}

      {/* Learning Tracks Section */}
      <LearningTracksSection />


      {/* Bundles Section */}
      <AIEssentialsBundleSection />

      {/* Ultimate Prompt Bundle Section */}
      <UltimatePromptBundleSection />

      {/* Stats Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20 relative overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)',
        }}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Transform Your Career</h2>
            <p className="text-xl text-gray-400">Join professionals mastering in-demand skills</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
                70+
              </div>
              <div className="text-gray-400">Expert Topics</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                15+
              </div>
              <div className="text-gray-400">Hours of Content</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-green-400 bg-clip-text text-transparent mb-2">
                100%
              </div>
              <div className="text-gray-400">Self-Paced Learning</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold bg-gradient-to-r from-green-400 to-yellow-400 bg-clip-text text-transparent mb-2">
                Lifetime
              </div>
              <div className="text-gray-400">Access Included</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-12 text-center overflow-hidden">
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Start Your Journey?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Recieve lifetime access to our courses today!
              </p>
              <Link
                to="/sign-up"
                className="bg-white text-black px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 hover:shadow-2xl inline-block text-center"
                onClick={() => handleCtaClick('cta-start-journey', 'Start Your Journey Today', 'bottom-cta')}
              >
                Start Your Journey Today
              </Link>
              {/* <p className="text-sm mt-4 text-white/70">Cancel anytime. No commitments.</p> */}
            </div>
          </div>
        </div>
      </section>

      {/* Theater Mode Modal */}
      {isTheaterMode && videoUrl && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={closeTheaterMode}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center transition-all hover:scale-110 border border-white/20 z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Video container */}
          <div className="relative w-full max-w-7xl mx-auto px-6">
            <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
              <video
                ref={theaterVideoRef}
                src={videoUrl}
                autoPlay
                muted={isMuted}
                loop
                playsInline
                className="w-full h-full object-contain"
              />

              {/* Control buttons */}
              <div className="absolute bottom-6 right-6 flex items-center space-x-3">
                {/* Mute/Unmute */}
                <button
                  onClick={toggleTheaterMute}
                  className="w-12 h-12 bg-black/80 backdrop-blur-xl rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl border border-white/20 hover:bg-black/90"
                >
                  {isMuted ? (
                    <VolumeX className="w-5 h-5 text-white" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </button>

                {/* Fullscreen */}
                <button
                  onClick={enterFullscreen}
                  className="w-12 h-12 bg-black/80 backdrop-blur-xl rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl border border-white/20 hover:bg-black/90"
                >
                  <Maximize2 className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-6 text-center">
              <p className="text-white/60 text-sm">
                Click the <X className="w-4 h-4 inline" /> button or press ESC to close • Click <Maximize2 className="w-4 h-4 inline" /> for fullscreen
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}